import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ApplePayIcon from '@/components/icons/apple-pay.svg?react'
import GooglePayIcon from '@/components/icons/google-pay.svg?react'
import { useScript } from '@/hooks/useScript'
import { useToast } from '@/hooks/useToast'
import i18n from '@/i18n'
import bitImg from '@/imgs/bit.png'
import { cn } from '@/lib/utils'
import { PaymentLinkResponse } from '@/pages/client-side/Payment/components/PaymentData'
import { BIT_MIN_ILS } from '@/pages/client-side/Payment/constants'

import { Button } from './ui/button'
import { Spinner } from './ui/spinner'

const HF_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thostedf.js'
const HF_APPLE_PAY_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thfapplepay.js'

function tranzilaHebrewEnglish(appLanguage: string | undefined): 'hebrew' | 'english' {
  return appLanguage?.toLowerCase().startsWith('he') ? 'hebrew' : 'english'
}

// `ApplePaySession` is only exposed by browsers that actually support Apple Pay JS
// in the current context (Safari on macOS/iOS, and any iOS WebKit browser where
// Apple has enabled it). Non-Safari desktop browsers and contexts without HTTPS
// will not expose it, so feature detection alone correctly gates the button.
function isApplePayJsSupported() {
  if (typeof window === 'undefined' || !('ApplePaySession' in window)) {
    return false
  }
  try {
    return Boolean(window.ApplePaySession?.canMakePayments?.())
  } catch {
    return false
  }
}

type HostedFieldKey = 'credit_card_number' | 'cvv' | 'expiry'

const INITIAL_FIELD_VALID: Record<HostedFieldKey, boolean> = {
  credit_card_number: false,
  cvv: false,
  expiry: false,
}

const INITIAL_BLURRED: Record<HostedFieldKey, boolean> = {
  credit_card_number: false,
  cvv: false,
  expiry: false,
}

function isHostedFieldKey(field: string): field is HostedFieldKey {
  return field === 'credit_card_number' || field === 'cvv' || field === 'expiry'
}

function getCommonParams(data: PaymentLinkResponse, i18nLanguage: string): TranzilaChargeCommonParams {
  const lang = tranzilaHebrewEnglish(i18nLanguage)

  return {
    terminal_name: data.terminalName,
    amount: data.sum,
    currency_code: 'ILS' as const,
    tran_mode: 'A',
    thtk: data.thtk,
    notify_url: data.notifyUrl,
    contact: data.contact,
    email: data.email,
    phone: data.phone,
    pdesc: data.pdesc,
    response_language: lang,
    ...(data.jsonPurchaseData && { json_purchase_data: data.jsonPurchaseData }),
    ...data.metadata,
  }
}

interface TranzilaFocusBlurEvent {
  type?: string
  field?: string
}

interface Props {
  paymentLinkData: PaymentLinkResponse
  onSuccess: (response: TranzilaChargeResponse) => void
  onError: (error: string) => void
}

interface FieldShellProps {
  fieldKey: HostedFieldKey
  label: string
  fieldId: string
  helperText?: React.ReactNode
  innerId: string
  innerClassName?: string
  ctx: {
    focusedField: HostedFieldKey | null
    hoveredField: HostedFieldKey | null
    fieldErrors: Record<string, string>
    fieldValid: Record<HostedFieldKey, boolean>
    blurredFields: Record<HostedFieldKey, boolean>
  }
  onMouseEnter: () => void
  onMouseLeave: () => void
}

function FieldShell({ fieldKey, label, fieldId, innerId, helperText, ctx, onMouseEnter, onMouseLeave }: FieldShellProps) {
  const serverErr = Boolean(ctx.fieldErrors[fieldKey])
  const showClientError = !ctx.fieldValid[fieldKey] && ctx.blurredFields[fieldKey]
  const showError = serverErr || showClientError
  const focused = ctx.focusedField === fieldKey
  const hovered = ctx.hoveredField === fieldKey && !focused

  return (
    <div className='flex w-full flex-col'>
      <div className='relative w-full pt-2' onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {/* Floating notched label (positioned at the top edge of the field, breaking the border) */}
        <label
          htmlFor={fieldId}
          className={cn(
            'absolute start-3 top-0 z-[2] bg-white px-1 text-xs leading-4 transition-colors',
            showError ? 'text-destructive' : focused ? 'text-icon-primary' : 'text-text-secondary'
          )}
        >
          {label}
        </label>

        {/* Field shell — Tranzila injects its iframe inside `innerId` */}
        <div
          id={innerId}
          className={cn(
            'tranzila-hf-field flex h-12 w-full items-center rounded-2xl border bg-white px-4 transition-all duration-200',
            showError && 'border-destructive shadow-[0_0_0_2px_rgba(255,0,0,0.2)]',
            !showError && focused && 'border-icon-primary shadow-[0_0_0_2px_rgba(0,174,255,0.2)]',
            !showError && !focused && hovered && 'border-[#B5BAC2]',
            !showError && !focused && !hovered && 'border-border-default'
          )}
        />
      </div>

      {helperText && (
        <p className={cn('mt-1 px-4 text-xs', showError ? 'text-destructive' : 'text-text-secondary')} aria-live='polite'>
          {helperText}
        </p>
      )}
    </div>
  )
}

export function TranzilaHostedFields({ paymentLinkData, onSuccess, onError }: Props) {
  const { t } = useTranslation()
  const { showErrorToast } = useToast()
  const fieldsRef = useRef<TranzilaHostedFieldsInstance | null>(null)
  const onErrorRef = useRef(onError)
  const onSuccessRef = useRef(onSuccess)
  const fieldValidRef = useRef<Record<HostedFieldKey, boolean>>({ ...INITIAL_FIELD_VALID })

  onErrorRef.current = onError
  onSuccessRef.current = onSuccess

  const [isReady, setIsReady] = useState(false)
  const [isCharging, setIsCharging] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [fieldValid, setFieldValid] = useState<Record<HostedFieldKey, boolean>>({ ...INITIAL_FIELD_VALID })
  const [focusedField, setFocusedField] = useState<HostedFieldKey | null>(null)
  const [hoveredField, setHoveredField] = useState<HostedFieldKey | null>(null)
  const [blurredFields, setBlurredFields] = useState<Record<HostedFieldKey, boolean>>({ ...INITIAL_BLURRED })
  const [googlePayAvailable, setGooglePayAvailable] = useState(false)
  // Google Pay does not work when the `cvv` hosted field is mounted, so we
  // recreate the instance without the cvv field for the duration of the gpay
  // charge and revert back to the normal config afterwards.
  const [gpayMode, setGpayMode] = useState(false)
  const pendingGpayChargeRef = useRef(false)
  const gpayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const paymentLinkDataRef = useRef(paymentLinkData)
  paymentLinkDataRef.current = paymentLinkData
  const scriptErrorNotifiedRef = useRef(false)

  const clearGpayTimeout = useCallback(() => {
    if (gpayTimeoutRef.current) {
      clearTimeout(gpayTimeoutRef.current)
      gpayTimeoutRef.current = null
    }
  }, [])

  const { status: hostedFieldsScriptStatus } = useScript(HF_SCRIPT_URL, {
    id: 'tranzila-hosted-fields-script',
    retry: true,
    maxRetries: 2,
    initialRetryDelay: 1000,
  })
  const { status: applePayScriptStatus } = useScript(HF_APPLE_PAY_SCRIPT_URL, {
    id: 'tranzila-hosted-fields-apple-script',
    retry: true,
    maxRetries: 2,
    initialRetryDelay: 1000,
  })

  const hfInstanceId = useMemo(() => `hf-${crypto.randomUUID().replace(/-/g, '')}`, [])

  const fieldIds = useMemo(
    () => ({
      credit_card_number: `${hfInstanceId}-card-number`,
      cvv: `${hfInstanceId}-cvv`,
      expiry: `${hfInstanceId}-expiry`,
    }),
    [hfInstanceId]
  )

  const selectors = useMemo(
    () => ({
      credit_card_number: `#${fieldIds.credit_card_number}`,
      cvv: `#${fieldIds.cvv}`,
      expiry: `#${fieldIds.expiry}`,
    }),
    [fieldIds]
  )

  const applePayAvailable = applePayScriptStatus === 'ready' && typeof hfChargeApple !== 'undefined' && isApplePayJsSupported()

  const clearHostContainers = useCallback(() => {
    for (const suffix of ['card-number', 'cvv', 'expiry'] as const) {
      document.getElementById(`${hfInstanceId}-${suffix}`)?.replaceChildren()
    }
  }, [hfInstanceId])

  const allFieldsValid = fieldValid.credit_card_number && fieldValid.cvv && fieldValid.expiry

  const shellCtx = useMemo(
    () => ({ focusedField, hoveredField, fieldErrors, fieldValid, blurredFields }),
    [focusedField, hoveredField, fieldErrors, fieldValid, blurredFields]
  )

  const applyChargeResult = useCallback(
    (err: TranzilaChargeError | null, response: TranzilaChargeResponse | null, mapFieldErrors: boolean) => {
      setIsCharging(false)
      if (err) {
        const messages = err.messages || []
        if (messages.length > 0) {
          if (mapFieldErrors) {
            const errorMap: Record<string, string> = {}
            messages.forEach(msg => {
              errorMap[msg.param] = msg.message
            })
            setFieldErrors(errorMap)
          }
          onErrorRef.current(messages.map(m => m.message).join(', '))
        } else {
          onErrorRef.current(i18n.t('payment.serverError'))
        }
        return
      }
      if (response) {
        if (response.transaction_response?.success) {
          onSuccessRef.current(response)
        } else {
          const errorMsg = response.transaction_response?.error || i18n.t('payment.serverError')
          onErrorRef.current(errorMsg)
        }
      }
    },
    []
  )

  useEffect(() => {
    let cancelled = false

    setIsReady(false)
    setFieldErrors({})
    setFieldValid({ ...INITIAL_FIELD_VALID })
    fieldValidRef.current = { ...INITIAL_FIELD_VALID }
    setBlurredFields({ ...INITIAL_BLURRED })
    setFocusedField(null)
    setHoveredField(null)
    setGooglePayAvailable(false)
    if (hostedFieldsScriptStatus !== 'ready') {
      return () => {
        cancelled = true
      }
    }

    if (typeof TzlaHostedFields === 'undefined') {
      onErrorRef.current(i18n.t('payment.serverError'))
      return () => {
        cancelled = true
      }
    }

    clearHostContainers()

    const instance = TzlaHostedFields.create({
      sandbox: false,
      autocomplete: true,
      fields: {
        credit_card_number: {
          selector: selectors.credit_card_number,
          placeholder: '',
          tabindex: 1,
        },
        expiry: {
          selector: selectors.expiry,
          placeholder: 'MM/YY',
          version: '1',
          tabindex: 2,
        },
        ...(gpayMode
          ? {}
          : {
              cvv: {
                selector: selectors.cvv,
                placeholder: '123',
                tabindex: 3,
              },
            }),
      },
      styles: {
        input: {
          'font-size': '16px',
          'font-family': 'Rubik, sans-serif',
          color: '#333',
          width: '100%',
          'text-align': 'right',
        },
        select: {
          'font-size': '14px',
          'font-family': 'Rubik, sans-serif',
          color: '#333',
          height: 'auto',
          width: '100%',
        },
      },
    })

    fieldsRef.current = instance

    instance.onEvent('ready', () => {
      if (!cancelled) {
        setIsReady(true)
      }
    })

    instance.onEvent('googleIsEnable', () => {
      if (cancelled) return
      setGooglePayAvailable(true)
      // Tranzila's GPay flow is only fully bootstrapped once `googleIsEnable`
      // fires — that's when the controlfields iframe has finished loading
      // `pay.js` + `ops_gpay.js` and `onGooglePaymentButtonClicked` has been
      // defined. Triggering chargeGpay any earlier (e.g. on `ready`) throws
      // `ReferenceError: onGooglePaymentButtonClicked is not defined` from
      // ops_controlfield.js. thostedf.js invokes this listener immediately
      // when registered after the event already fired, so it works for both
      // first-time and recreated instances.
      if (pendingGpayChargeRef.current) {
        pendingGpayChargeRef.current = false
        clearGpayTimeout()
        const gpayParams: TranzilaChargeGpayParams = {
          ...getCommonParams(paymentLinkDataRef.current, i18n.language),
        }
        instance.chargeGpay(gpayParams, (err, response) => {
          // Hide the form before recreating with cvv to avoid a flicker
          // showing the cvv-less form between the gpay sheet closing and the
          // re-creation effect running.
          setIsReady(false)
          applyChargeResult(err, response, false)
          setGpayMode(false)
        })
      }
    })

    instance.onEvent('focus', (msg: unknown) => {
      const m = msg as TranzilaFocusBlurEvent & { fieldName?: string }
      const fieldKey = m.field ?? m.fieldName
      if (!fieldKey || !isHostedFieldKey(fieldKey)) return
      if (!cancelled) setFocusedField(fieldKey)
    })

    instance.onEvent('blur', (msg: unknown) => {
      const m = msg as TranzilaFocusBlurEvent & { fieldName?: string }
      const fieldKey = m.field ?? m.fieldName
      if (!fieldKey || !isHostedFieldKey(fieldKey)) return
      if (cancelled) return
      setFocusedField(prev => (prev === fieldKey ? null : prev))
      setBlurredFields(prev => ({ ...prev, [fieldKey]: true }))
    })

    instance.onEvent('validityChange', (event: unknown) => {
      const e = event as { field?: string; isValid?: boolean }
      if (!e.field || !isHostedFieldKey(e.field)) return
      const key = e.field
      const valid = Boolean(e.isValid)
      fieldValidRef.current[key] = valid
      if (!cancelled) {
        setFieldValid(prev => ({ ...prev, [key]: valid }))
        if (valid) {
          setFieldErrors(prev => {
            const next = { ...prev }
            delete next[key]
            return next
          })
        }
      }
    })

    return () => {
      cancelled = true
      fieldsRef.current = null
      clearHostContainers()
    }
  }, [paymentLinkData.thtk, clearHostContainers, selectors, hostedFieldsScriptStatus, gpayMode, applyChargeResult, clearGpayTimeout])

  useEffect(() => {
    if (hostedFieldsScriptStatus === 'ready') {
      scriptErrorNotifiedRef.current = false
      return
    }
    if (hostedFieldsScriptStatus === 'error' && !scriptErrorNotifiedRef.current) {
      scriptErrorNotifiedRef.current = true
      onErrorRef.current(i18n.t('payment.serverError'))
    }
  }, [hostedFieldsScriptStatus])

  useEffect(() => {
    return () => {
      clearGpayTimeout()
    }
  }, [clearGpayTimeout])

  const handleCharge = useCallback(() => {
    if (!fieldsRef.current || isCharging) return

    const v = fieldValidRef.current
    if (!v.credit_card_number || !v.cvv || !v.expiry) {
      showErrorToast(t('payment.fillCardFields'))
      return
    }

    setIsCharging(true)

    fieldsRef.current.charge(
      {
        ...getCommonParams(paymentLinkData, i18n.language),
      },
      (err, response) => {
        applyChargeResult(err, response, true)
      }
    )
  }, [applyChargeResult, paymentLinkData, isCharging, showErrorToast, t])

  const handleChargeBit = useCallback(() => {
    if (!fieldsRef.current || isCharging) return

    if (paymentLinkData.sum < BIT_MIN_ILS) {
      showErrorToast(t('payment.bitMinimumAmount'))
      return
    }

    const lang = tranzilaHebrewEnglish(i18n.language)
    setIsCharging(true)

    const bitParams: TranzilaChargeBitParams = {
      ...getCommonParams(paymentLinkData, lang),
      bit_language: lang,
      transaction_layout: 'qr-sms',
    }

    fieldsRef.current.chargeBit(bitParams, (err, response) => {
      applyChargeResult(err, response, false)
    })
  }, [applyChargeResult, paymentLinkData, isCharging, showErrorToast, t])

  const handleChargeGpay = useCallback(() => {
    if (!fieldsRef.current || isCharging || pendingGpayChargeRef.current) return

    // Tranzila's Google Pay flow fails when the cvv hosted field is mounted,
    // so we flag a pending charge, hide the current form, and switch into
    // gpayMode. The instance-creation effect will then rebuild Tranzila
    // without cvv and the `googleIsEnable` handler will dispatch the
    // chargeGpay call once Tranzila's controlfields iframe has finished
    // loading `pay.js` + `ops_gpay.js`.
    pendingGpayChargeRef.current = true
    setIsCharging(true)
    setIsReady(false)
    setGpayMode(true)

    // Safety net: if `googleIsEnable` never fires (no GPay wallet, scripts
    // failed to load, etc.) revert to the normal form rather than leaving the
    // user stuck staring at a spinner.
    clearGpayTimeout()
    gpayTimeoutRef.current = setTimeout(() => {
      gpayTimeoutRef.current = null
      if (!pendingGpayChargeRef.current) return
      pendingGpayChargeRef.current = false
      setIsCharging(false)
      setGpayMode(false)
      onErrorRef.current(i18n.t('payment.serverError'))
    }, 15000)
  }, [clearGpayTimeout, isCharging])

  const handleChargeApple = useCallback(() => {
    if (isCharging || !applePayAvailable || typeof hfChargeApple === 'undefined' || !isApplePayJsSupported()) return

    const responseLang = tranzilaHebrewEnglish(i18n.language)
    setIsCharging(true)

    const appleParams: TranzilaChargeAppleParams = {
      ...getCommonParams(paymentLinkData, responseLang),
      labelApple: paymentLinkData.pdesc ?? 'Shipping Payment',
    }

    hfChargeApple(appleParams, (err, response) => {
      applyChargeResult(err, response, false)
    })
  }, [applePayAvailable, applyChargeResult, paymentLinkData, isCharging])

  const bitEligible = paymentLinkData.sum >= BIT_MIN_ILS

  const fieldHelper = (key: HostedFieldKey): React.ReactNode => {
    const server = fieldErrors[key]
    const clientInvalid = !fieldValid[key] && blurredFields[key]
    if (!server && !clientInvalid) return null
    return server ?? t('payment.invalidField')
  }

  const hasAnyAltMethod = googlePayAvailable || applePayAvailable || bitEligible

  return (
    <div className='flex flex-col gap-6'>
      {/* Header */}
      <header className='flex flex-col gap-2'>
        <h1 className='text-text-primary text-[22px] leading-7 font-medium'>{t('payment.paymentTitle')}</h1>
        <p className='text-text-secondary text-sm leading-5'>
          {t('payment.paymentSubtitleLine1')}
          <br />
          {t('payment.paymentSubtitleLine2')}
        </p>
      </header>

      {/* Payment card */}
      <section className='flex flex-col gap-4 rounded-xl bg-white p-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_0_rgba(0,0,0,0.05)]'>
        <div className='flex items-center justify-between py-2'>
          <h2 className='text-text-primary text-base leading-6 font-medium'>
            {t('payment.totalLabel')}: ₪{paymentLinkData.sum}
          </h2>
        </div>

        {(!isReady || gpayMode) && (
          <div className='flex items-center justify-center py-8'>
            <Spinner className='size-8' />
          </div>
        )}

        <div className={cn('flex flex-col gap-4', (!isReady || gpayMode) && 'invisible h-0 overflow-hidden')}>
          <FieldShell
            fieldKey='credit_card_number'
            label={t('payment.cardNumber')}
            fieldId={fieldIds.credit_card_number}
            innerId={fieldIds.credit_card_number}
            ctx={shellCtx}
            helperText={fieldHelper('credit_card_number')}
            onMouseEnter={() => setHoveredField('credit_card_number')}
            onMouseLeave={() => setHoveredField(null)}
          />

          <div className='grid grid-cols-2 gap-4'>
            <FieldShell
              fieldKey='expiry'
              label={t('payment.expiryDate')}
              fieldId={fieldIds.expiry}
              innerId={fieldIds.expiry}
              ctx={shellCtx}
              helperText={fieldHelper('expiry')}
              onMouseEnter={() => setHoveredField('expiry')}
              onMouseLeave={() => setHoveredField(null)}
            />
            <FieldShell
              fieldKey='cvv'
              label={t('payment.cvv')}
              fieldId={fieldIds.cvv}
              innerId={fieldIds.cvv}
              ctx={shellCtx}
              helperText={fieldHelper('cvv')}
              onMouseEnter={() => setHoveredField('cvv')}
              onMouseLeave={() => setHoveredField(null)}
            />
          </div>

          <p className='text-text-secondary text-center text-xs'>{t('payment.securePayment')}</p>
        </div>
      </section>

      {/* Primary CTA — yellow continue button */}
      <Button onClick={handleCharge} loading={isCharging} disabled={!isReady || isCharging || !allFieldsValid} className='w-full' size='lg'>
        {t('payment.continueToPaymentProcess')}
      </Button>

      {/* Alternative payment methods */}
      {hasAnyAltMethod && (
        <div className='flex flex-col gap-3'>
          <div className='text-text-secondary flex items-center gap-3 text-xs'>
            <span className='bg-border-default h-px flex-1' aria-hidden />
            <span>{t('payment.alternativePaymentDivider')}</span>
            <span className='bg-border-default h-px flex-1' aria-hidden />
          </div>

          {applePayAvailable && (
            <Button
              type='button'
              variant='outline'
              size='lg'
              aria-label='Apple Pay'
              onClick={handleChargeApple}
              disabled={!isReady || isCharging}
            >
              <ApplePayIcon className='size-14' />
            </Button>
          )}
          {googlePayAvailable && (
            <Button
              type='button'
              variant='outline'
              size='lg'
              aria-label='Google Pay'
              onClick={handleChargeGpay}
              disabled={!isReady || isCharging}
            >
              <GooglePayIcon className='size-14' />
            </Button>
          )}

          {bitEligible && (
            <button
              type='button'
              onClick={handleChargeBit}
              disabled={!isReady || isCharging}
              className='flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#003942] text-white transition-all duration-200 hover:bg-[#00485a] active:bg-[#002831] disabled:cursor-not-allowed disabled:opacity-50'
            >
              <span className='font-medium'>{t('payment.payWithBitButton')}</span>
              <img src={bitImg} alt='' className='size-6 rounded-md' />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
